import { useState, useEffect } from "react";
import { Panel } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyField, FieldRow, Line, TextField, Divider } from "./fields";
import { ArrowLeftRight } from "lucide-react";
import api from "@/dealership/lib/api";

export default function TradePanel({ ws, set, totals, internal }) {
  const [appraisals, setAppraisals] = useState([]);
  const setTrade = (patch) => set({ trade: { ...(ws.trade || {}), ...patch } });

  useEffect(() => {
    if (!ws.has_trade) return;
    api.get("/appraisals").then(({ data }) => setAppraisals(data || [])).catch(() => setAppraisals([]));
  }, [ws.has_trade]);

  const pullAppraisal = (id) => {
    const a = appraisals.find((x) => x.id === id);
    if (!a) return;
    const v = a.appraisal || {};
    setTrade({
      appraisal_id: id,
      desc: `${a.year || ""} ${a.make || ""} ${a.model || ""} ${a.trim || ""}`.trim(),
      vin: a.vin || "",
      year: a.year, make: a.make || "", model: a.model || "",
      odometer: a.odometer || 0,
      allowance: Number(v.trade_in_high || v.trade_in_low || 0),
      acv: Number(v.trade_in_low || v.trade_in_high || 0),
    });
  };

  return (
    <Panel
      label="Trade-in"
      title={ws.has_trade ? (ws.trade?.desc || "Customer trade") : "No trade"}
      icon={ArrowLeftRight}
      testid="desk-trade-panel"
      actions={
        <Button size="sm" variant={ws.has_trade ? "outline" : "default"} onClick={() => set({ has_trade: !ws.has_trade })} data-testid="desk-toggle-trade">
          {ws.has_trade ? "Remove trade" : "Add trade"}
        </Button>
      }
    >
      {!ws.has_trade ? (
        <p className="text-sm text-muted-foreground">
          No trade on this bill of sale — HST applies to the full selling price.
        </p>
      ) : (
        <div className="space-y-3">
          {appraisals.length > 0 && (
            <div>
              <div className="label-mono mb-2">Pull from an appraisal</div>
              <Select value={ws.trade?.appraisal_id || ""} onValueChange={pullAppraisal}>
                <SelectTrigger className="h-9" data-testid="desk-appraisal-select"><SelectValue placeholder="Select an appraisal…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {appraisals.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.year} {a.make} {a.model} — {a.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <TextField value={ws.trade?.desc} onChange={(v) => setTrade({ desc: v })} placeholder="Year Make Model" testid="desk-trade-desc" />
            <TextField value={ws.trade?.vin} onChange={(v) => setTrade({ vin: v })} placeholder="VIN" testid="desk-trade-vin" />
          </div>
          <FieldRow label="Trade allowance" hint="What the customer sees">
            <MoneyField value={ws.trade?.allowance} onChange={(v) => setTrade({ allowance: v })} testid="desk-trade-allowance" />
          </FieldRow>
          {internal && (
            <FieldRow label="ACV — actual cash value" hint="Internal only">
              <MoneyField value={ws.trade?.acv} onChange={(v) => setTrade({ acv: v })} testid="desk-trade-acv" />
            </FieldRow>
          )}
          <FieldRow label="Lien payout">
            <MoneyField value={ws.trade?.lien_amount} onChange={(v) => setTrade({ lien_amount: v })} testid="desk-trade-lien" />
          </FieldRow>
          <div className="flex items-center gap-3">
            <div className="flex-1 label-mono">Lienholder</div>
            <TextField value={ws.trade?.lienholder} onChange={(v) => setTrade({ lienholder: v })} placeholder="Bank / finance co." className="w-48" testid="desk-lienholder" />
          </div>
          <Divider />
          <Line label="Net trade equity" value={totals.net_trade_equity} bold gold={totals.net_trade_equity >= 0} negative={totals.net_trade_equity < 0} testid="desk-net-equity" />
          {totals.negative_equity > 0 && (
            <p className="text-xs text-amber-400" data-testid="desk-negative-equity-note">
              Negative equity of {totals.negative_equity.toLocaleString("en-CA", { style: "currency", currency: "CAD" })} is rolled into the amount financed.
            </p>
          )}
          {totals.tax_savings_from_trade > 0 && (
            <p className="text-xs text-emerald-400" data-testid="desk-tax-savings">
              Customer saves {totals.tax_savings_from_trade.toLocaleString("en-CA", { style: "currency", currency: "CAD" })} in HST by trading in.
            </p>
          )}
          {internal && totals.over_allowance > 0 && (
            <p className="text-xs text-muted-foreground">Over-allowance {totals.over_allowance.toLocaleString("en-CA", { style: "currency", currency: "CAD" })} — comes out of front gross.</p>
          )}
        </div>
      )}
    </Panel>
  );
}
